// Copyright ©️ 2026 eVolve MEP, LLC

import { formatDate } from '@evolvemep/api-ts';
import { Button, Stack, Text, Title, Flex, Badge, Avatar } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { MobileBasePageHeader } from 'components/Mantine/MobileBasePageHeader';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { UserAvatar } from 'components/Mantine/UserInfo';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { WorkOrderItemsList } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemsList';
import { WorkOrderDetailModalProvider } from 'modules/Shop/WorkOrders/WorkOrderDetail/modalRegistries';
import { useWorkOrderDetailMutations } from 'modules/Shop/WorkOrders/WorkOrderDetail/useWorkOrderDetailMutations';
import type { WorkOrderDetailPageProps } from 'modules/Shop/WorkOrders/WorkOrderDetail/WorkOrderDetailPage';
import { MobileWorkOrderActions } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/MobileWorkOrderActions';

export const MobileWorkOrderDetailPage = ({ workOrder, onWorkOrderUpdated }: WorkOrderDetailPageProps) => {
  const navigate = useEvolveNavigate(workOrder.identifier);

  const { refreshWorkOrder } = useWorkOrderDetailMutations(workOrder, onWorkOrderUpdated);

  return (
    <WorkOrderDetailModalProvider>
      <Stack flex={1} mih={0} w="100%" p="md" pb={0} gap="xs">
        <MobileBasePageHeader
          title={workOrder.identifier}
          parentPage={{ title: 'Work Orders', url: '/work-orders' }}
          rightSideComponent={<MobileWorkOrderActions workOrder={workOrder} onWorkOrderUpdated={onWorkOrderUpdated} />}
        />
        <Stack flex={1} style={{ overflowY: 'auto' }} gap="md">
          <Stack gap={0}>
            <Text c="dimmed" fz="sm">
              {workOrder.identifier}
            </Text>
            <Title order={3}>{workOrder.name}</Title>
          </Stack>

          {!!workOrder.description && <Text style={{ whiteSpace: 'pre-wrap' }}>{workOrder.description}</Text>}

          <Button.Group orientation="vertical">
            <Button
              variant="light"
              justify="space-between"
              rightSection={<EvolveIcon icon="ArrowRight" size="xs" />}
              onClick={() => navigate(`/work-orders/${workOrder.workOrderId}/details`)}
              px="xs"
              size="md"
            >
              <Flex gap="xs">
                <EntityStatusBadge
                  customStatusEntityName="Work Order"
                  stateName={workOrder.workOrderStateName}
                  statusId={workOrder.workOrderStatusId}
                />
                <Badge variant="light" size="lg" leftSection={<EvolveIcon icon="CalendarBoard" size="xs" />}>
                  {formatDate(workOrder.needByDate, 'MM/dd/yyyy')}
                </Badge>
                <Avatar.Group>
                  {workOrder.assignees.map((assignee) => (
                    <UserAvatar key={assignee.userId} name={validateUserNameOrEmail(assignee)} size="sm" />
                  ))}
                </Avatar.Group>
              </Flex>
            </Button>

            <Button
              variant="light"
              justify="space-between"
              rightSection={<EvolveIcon icon="ArrowRight" size="xs" />}
              onClick={() => navigate(`/work-orders/${workOrder.workOrderId}/attachments`)}
              px="xs"
              size="md"
            >
              <Flex gap="xs" align="center">
                Attachments
                <Text c="dimmed">({workOrder.totalAttachmentCount})</Text>
              </Flex>
            </Button>

            <Button
              variant="light"
              justify="space-between"
              rightSection={<EvolveIcon icon="ArrowRight" size="xs" />}
              onClick={() => navigate(`/work-orders/${workOrder.workOrderId}/activity`)}
              px="xs"
              size="md"
            >
              Activity
            </Button>
          </Button.Group>

          <WorkOrderItemsList
            workOrder={workOrder}
            disableSelection
            forceWorkOrderRefresh={refreshWorkOrder}
            onItemAttachmentsChanged={refreshWorkOrder}
            onTaskAttachmentsChanged={refreshWorkOrder}
          />
        </Stack>
      </Stack>
    </WorkOrderDetailModalProvider>
  );
};
